import { baseApi } from '@store/baseApi';

import type {
  BatchArchiveRequest,
  BatchArchiveResponse,
  CleanupApplyRequest,
  CleanupApplyResponse,
  PilotFixture,
} from '../types';

/**
 * Practice cleanup wizard API (GDN-2295 / GDN-2296 / GDN-2320).
 *
 * The bootstrap read returns the pilot-fixture row shape verbatim
 * (`PilotFixture`), so its response flows straight into `deriveWizardData`
 * with no adapter — the fixture format IS the API contract. Hand-injected
 * (no generated OpenAPI client) as the feature's own endpoints.
 *
 * Apply fans across three verbs: `batchArchive` (archives/restores/freed
 * templates, GDN-2294), `cleanupApply` (promote/merge, GDN-2320), and
 * `renameEncounterType` (set-aside PATCH renames).
 */
export const practiceCleanupApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCleanupBootstrap: builder.query<PilotFixture, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `/v2/provider/organizations/${orgId}/encounter-types/cleanup-bootstrap`,
      }),
    }),
    batchArchiveEncounterTypes: builder.mutation<
      BatchArchiveResponse,
      { orgId: string; body: BatchArchiveRequest }
    >({
      query: ({ orgId, body }) => ({
        url: `/v2/provider/organizations/${orgId}/encounter-types/batch-archive`,
        method: 'POST',
        body,
      }),
    }),
    cleanupApply: builder.mutation<
      CleanupApplyResponse,
      { orgId: string; body: CleanupApplyRequest }
    >({
      query: ({ orgId, body }) => ({
        url: `/v2/provider/organizations/${orgId}/encounter-types/cleanup-apply`,
        method: 'POST',
        body,
      }),
    }),
    renameEncounterType: builder.mutation<
      unknown,
      { orgId: string; typeId: string; name: string }
    >({
      query: ({ orgId, typeId, name }) => ({
        url: `/v2/provider/organizations/${orgId}/encounter-types/${typeId}`,
        method: 'PATCH',
        body: { encounter_type: name },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCleanupBootstrapQuery,
  useBatchArchiveEncounterTypesMutation,
  useCleanupApplyMutation,
  useRenameEncounterTypeMutation,
} = practiceCleanupApi;
